import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Tooltip, Typography } from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import axios from "axios";
import { acme } from "../../theme/acmeTheme";

function formatCredits(value) {
  const n = Math.max(0, Math.floor(Number(value) || 0));
  return n.toLocaleString();
}

/**
 * Small SMS credits balance shown left of the avatar in the top bar.
 * Click navigates to Billing → SMS Marketing Plan.
 */
export default function SmsBalanceChip({ compact = false }) {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/sms-campaigns/credits");
      setWallet({
        credits: data.smsCredits ?? 0,
        unlimited: !!data.unlimitedSms,
      });
    } catch {
      setWallet(null);
    }
  }, []);

  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    const id = setInterval(load, 60000);
    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(id);
    };
  }, [load]);

  if (!wallet) return null;

  const label = wallet.unlimited
    ? "Unlimited"
    : formatCredits(wallet.credits);

  return (
    <Tooltip title="SMS credits — click to buy more">
      <Box
        onClick={() => navigate("/billing", { state: { activeTab: 2 } })}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          px: 1.2,
          py: 0.5,
          borderRadius: 999,
          backgroundColor: acme.colors.brandMuted,
          border: `1px solid ${acme.colors.brand}80`,
          transition: "background-color .2s",
          "&:hover": { backgroundColor: `${acme.colors.brand}22` },
        }}
      >
        <AccountBalanceWalletIcon sx={{ fontSize: 18, color: acme.colors.brand }} />
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, color: acme.colors.foreground, whiteSpace: "nowrap" }}
        >
          {label}
        </Typography>
        {!compact && (
          <Typography
            variant="caption"
            sx={{ color: acme.colors.brand, display: { xs: "none", md: "inline" } }}
          >
            credits
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}
